import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { SectionIntro } from './alt/SectionIntro';
import { TALK_TO_SALES_URL } from '../lib/links';
import { ScrubVideoPlate, CLOSING_VIDEO, CLOSING_POSTER } from './ScrubVideoPlate';
function FeatureItem({
  text
}: {
  text: string;
}) {
  // A bare tick, matching the pricing cards above. The bordered disc made five
  // list rows read as five badges.
  return <li className="flex items-start gap-3.5 text-white/75" data-id="element-4557" data-mp="2ao9nds2n0uuo">
      <Check size={17} strokeWidth={2.6} className="mt-[3px] shrink-0 text-[#FB923C]" data-id="element-4558" data-mp="2eojvba9zpvc6" />
      <span className="text-[15.5px] leading-relaxed" data-id="element-4559" data-mp="2jmlmkihkl0yq">{text}</span>
    </li>;
}

/**
 * The page's closing panel.
 *
 * It used to be an ink card with a 2.5rem radius floating inside the cream
 * page, carrying a glassmorphic sub-card that restated the $100 offer the
 * paragraph directly above it had already made. Two problems: a rounded card
 * is a smaller gesture than an ending needs, and the site's other closings —
 * the home page and every feature page — are full-bleed ink. This is now that
 * same panel, so the bottom of the pricing page is the bottom of the site.
 *
 * The glass card is gone and its content folded into the one line under the
 * actions, which is where every other surface states the offer.
 */
export function PricingClosingCTA() {
  return <section className="relative overflow-hidden bg-[#1A120C] py-20 text-white md:py-28" data-id="element-4560" data-mp="3ex9q25mglzni">
      <ScrubVideoPlate src={CLOSING_VIDEO} poster={CLOSING_POSTER} data-id="element-4561" data-mp="3a6kx0lv4sum9" />
      <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-4562" data-mp="3a59sukxrxkum" />
      <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-4563" data-mp="362vae1y0on3v">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-20" data-id="element-4564" data-mp="316xt9texjcq1">
          <SectionIntro align="left" className="lg:col-span-7" data-id="element-4565" data-mp="2w71q1we23pbj">
            <h2 className="font-title text-[36px] font-bold leading-[1.04] tracking-[-0.03em] text-[#FAF8F4] sm:text-[46px] md:text-[56px]" data-id="element-4566" data-mp="2r7p2taj9wg8f">
              Start free. Pay only
              <br data-id="element-4567" data-mp="2hm60bippschd" />
              when you're ready.
            </h2>
            <p className="mt-6 max-w-[54ch] text-[16.5px] leading-[1.7] text-white/60 md:text-[17.5px]" data-id="element-4568" data-mp="2mjz4y7bdcyfj">
              Every feature. Every integration. $100 in credits on the house. No
              credit card, no sales call, no catch. When you need more, it
              starts $50/month.
            </p>

            <div className="mt-9 flex flex-col items-stretch gap-x-8 gap-y-4 sm:flex-row sm:items-center" data-id="element-4569" data-mp="30b49nntlggwq">
              <WaitlistCTA analyticsLabel="pricing-closing" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-4570" data-mp="358w00delbuyt">
                Get started free <ArrowRight size={16} data-id="element-4571" data-mp="3j7c4rysw7beb" />
              </WaitlistCTA>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:text-white/70" data-id="element-4572" data-mp="3o0yky0y9lxev">
                Talk to sales
              </a>
            </div>
            <p className="mt-7 text-[13px] text-white/45" data-id="element-4573" data-mp="1m9y2vql0at7w">
              $100 in credits included · no card needed
            </p>

          </SectionIntro>

          {/* What the free credit actually buys, kept beside the offer rather
           than beneath it so the panel reads as one statement across the
           width instead of a stack the reader scrolls through. */}
          <SectionIntro align="left" className="lg:col-span-5 lg:pt-4" data-id="element-4574" data-mp="13l19rq1ci6r9">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-white/35" data-id="element-4575" data-mp="yleu79y1oefa">
              Included from day one
            </p>
            <ul className="mt-6 space-y-4 border-t border-white/10 pt-6" data-id="element-4576" data-mp="2eylue3wcmetw">
              <FeatureItem text="Slack-native — works in threads & @mentions" data-id="element-4577" data-mp="2a13hy3j4hhh5" />
              <FeatureItem text="Connects to the tools you already use" data-id="element-4578" data-mp="jjgeanl8p8ly" />
              <FeatureItem text="Runs real tasks, not just answers" data-id="element-4579" data-mp="jlayu0slmypi" />
              <FeatureItem text="Scheduled tasks & proactive check-ins" data-id="element-4580" data-mp="ojkz2pt6kphz" />
              <FeatureItem text="Reports, recaps & ready-to-send drafts" data-id="element-4581" data-mp="sjn0qyyx1ix9" />
            </ul>
          </SectionIntro>
        </div>
      </div>
    </section>;
}